import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  UserPlus, MoreHorizontal, Trash2, Shield, KeyRound, Mail,
  RefreshCw, Loader2, CheckCircle2, Clock, Send,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', role: 'consultant' });
  const [passwordDialog, setPasswordDialog] = useState(null); // user object
  const [newPassword, setNewPassword] = useState('');

  const fetchUsers = async () => {
    try {
      const { data } = await axios.get(`${API}/users`, { withCredentials: true });
      setUsers(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleInvite = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/users/invite`, form, { withCredentials: true });
      if (data.email_sent) {
        toast.success(`Invitation envoyee a ${data.email}`);
      } else {
        toast.warning(`Utilisateur cree mais email non envoye. ${data.error || ''}`);
      }
      setInviteOpen(false);
      setForm({ name: '', email: '', role: 'consultant' });
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSending(false);
    }
  };

  const handleResendInvite = async (userId) => {
    try {
      const { data } = await axios.post(`${API}/users/${userId}/resend-invite`, {}, { withCredentials: true });
      toast.success(data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur envoi');
    }
  };

  const handleSendResetLink = async (userId) => {
    try {
      const { data } = await axios.post(`${API}/users/${userId}/reset-link`, {}, { withCredentials: true });
      toast.success(data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur envoi');
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/users/${userId}/role`, { role: newRole }, { withCredentials: true });
      toast.success('Role mis a jour');
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (userId, name) => {
    if (!window.confirm(`Supprimer l'utilisateur "${name}" ? Cette action est irreversible.`)) return;
    try {
      await axios.delete(`${API}/users/${userId}`, { withCredentials: true });
      toast.success('Utilisateur supprime');
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleSetPassword = async () => {
    if (!passwordDialog || newPassword.length < 6) return;
    try {
      await axios.put(`${API}/settings/users/${passwordDialog.id}/password`, { new_password: newPassword }, { withCredentials: true });
      toast.success(`Mot de passe defini pour ${passwordDialog.name}`);
      setPasswordDialog(null);
      setNewPassword('');
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'invited') {
      return (
        <span className="inline-flex items-center gap-1 text-xs font-mono px-2 py-1 rounded bg-amber-50 text-amber-700">
          <Clock className="w-3 h-3" /> En attente
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-xs font-mono px-2 py-1 rounded bg-green-50 text-green-700">
        <CheckCircle2 className="w-3 h-3" /> Actif
      </span>
    );
  };

  const getRoleBadge = (role) => {
    if (role === 'admin') {
      return (
        <span className="inline-flex items-center gap-1 text-xs font-mono px-2 py-1 rounded bg-[#002FA7]/10 text-[#002FA7]">
          <Shield className="w-3 h-3" /> Admin
        </span>
      );
    }
    if (role === 'admin_finance') {
      return (
        <span className="inline-flex items-center gap-1 text-xs font-mono px-2 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
          <Shield className="w-3 h-3" /> Admin Finance
        </span>
      );
    }
    return (
      <span className="text-xs font-mono px-2 py-1 rounded bg-neutral-100 text-neutral-600">
        Consultant
      </span>
    );
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="user-management-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-neutral-900">Utilisateurs</h1>
            <p className="text-neutral-500 mt-1">Gerez les comptes et les acces a la plateforme</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchUsers} data-testid="refresh-users-btn">
              <RefreshCw className="w-4 h-4 mr-1" /> Rafraichir
            </Button>
            <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#002FA7] hover:bg-[#002277]" data-testid="invite-user-btn">
                  <UserPlus className="w-4 h-4 mr-2" /> Inviter un utilisateur
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading">Inviter un utilisateur</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleInvite} className="space-y-4 mt-4">
                  <div>
                    <Label htmlFor="inv-name">Nom complet *</Label>
                    <Input id="inv-name" value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Jean Dupont" required className="mt-1" data-testid="invite-name-input" />
                  </div>
                  <div>
                    <Label htmlFor="inv-email">Email *</Label>
                    <Input id="inv-email" type="email" value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="consultant@example.com" required className="mt-1" data-testid="invite-email-input" />
                  </div>
                  <div>
                    <Label htmlFor="inv-role">Role</Label>
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                      <SelectTrigger className="mt-1" data-testid="invite-role-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="consultant">Consultant</SelectItem>
                        <SelectItem value="admin">Administrateur</SelectItem>
                        <SelectItem value="admin_finance">Admin Finance (accès Résultats)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-xs text-neutral-400">
                    L'utilisateur recevra un email avec un lien pour definir son mot de passe. Le lien est valable 7 jours.
                  </p>
                  <div className="flex justify-end gap-3 pt-2">
                    <Button type="button" variant="outline" onClick={() => setInviteOpen(false)}>Annuler</Button>
                    <Button type="submit" className="bg-[#002FA7] hover:bg-[#002277]" disabled={sending}
                      data-testid="send-invite-btn">
                      {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                      Envoyer l'invitation
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Users table */}
        <div className="bg-white border border-neutral-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-2 border-neutral-300 border-t-[#002FA7] rounded-full animate-spin mx-auto" />
            </div>
          ) : users.length === 0 ? (
            <div className="p-12 text-center">
              <UserPlus className="w-12 h-12 mx-auto mb-4 text-neutral-300" />
              <p className="text-neutral-500">Aucun utilisateur</p>
            </div>
          ) : (
            <table className="w-full data-table" data-testid="users-table">
              <thead>
                <tr>
                  <th>Utilisateur</th>
                  <th>Role</th>
                  <th>Statut</th>
                  <th>Cree le</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-[#002FA7]/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-[#002FA7] text-sm font-bold">
                            {u.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-neutral-900 text-sm">{u.name}</p>
                          <p className="text-xs text-neutral-500 truncate">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td>{getRoleBadge(u.role)}</td>
                    <td>{getStatusBadge(u.status)}</td>
                    <td className="text-neutral-500 text-xs font-mono">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString('fr-FR') : '-'}
                    </td>
                    <td>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`user-menu-${u.id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {u.status === 'invited' && (
                            <DropdownMenuItem onClick={() => handleResendInvite(u.id)}>
                              <Mail className="w-4 h-4 mr-2" /> Renvoyer l'invitation
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => { setPasswordDialog(u); setNewPassword(''); }}>
                            <KeyRound className="w-4 h-4 mr-2" /> Definir le mot de passe
                          </DropdownMenuItem>
                          {u.status === 'active' && (
                            <DropdownMenuItem onClick={() => handleSendResetLink(u.id)}>
                              <Mail className="w-4 h-4 mr-2" /> Envoyer lien de reset
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => handleChangeRole(u.id, u.role === 'admin' ? 'consultant' : 'admin')}>
                            <Shield className="w-4 h-4 mr-2" />
                            {u.role === 'admin' ? 'Passer Consultant' : 'Passer Admin'}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleChangeRole(u.id, 'admin_finance')} disabled={u.role === 'admin_finance'}>
                            <Shield className="w-4 h-4 mr-2 text-emerald-600" />
                            Passer Admin Finance
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDelete(u.id, u.name)} className="text-red-600">
                            <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Set Password Dialog */}
        <Dialog open={!!passwordDialog} onOpenChange={(open) => { if (!open) { setPasswordDialog(null); setNewPassword(''); } }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <KeyRound className="w-5 h-5 text-[#002FA7]" />
                Definir le mot de passe
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="bg-neutral-50 rounded p-3">
                <p className="text-sm font-medium text-neutral-900">{passwordDialog?.name}</p>
                <p className="text-xs text-neutral-500">{passwordDialog?.email}</p>
              </div>
              <div>
                <Label htmlFor="set-pwd">Nouveau mot de passe</Label>
                <Input id="set-pwd" type="text" value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimum 6 caracteres"
                  className="mt-1 font-mono" data-testid="set-password-input" />
                <p className="text-xs text-neutral-400 mt-1">Communiquez ce mot de passe au consultant par telephone ou message.</p>
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setPasswordDialog(null)}>Annuler</Button>
                <Button className="bg-[#002FA7] hover:bg-[#002277]" onClick={handleSetPassword}
                  disabled={newPassword.length < 6} data-testid="confirm-set-password-btn">
                  Enregistrer
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
